<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-8">
        <page-header class="mb-0!">Intelligent Inbox</page-header>

        <vx-expandable-card color="info">
            <template v-slot:content>
                <div class="flex flex-col gap-6">
                    <div>
                        <span class="font-extrabold text-2xl text-slate-700">Categories</span>
                        <p class="text-sm text-slate-500 mb-0">
                            Configure how the AI classifies and prioritizes conversations.
                        </p>
                    </div>

                    <page-loading v-if="loading"></page-loading>

                    <vx-alert v-if="!loading && loadError" color="danger" :closeable="false" shadow>
                        <div class="flex items-center justify-between">
                            Failed to load categories
                            <vx-button data-test="retry-btn" @click="fetchCategories" size="sm" color="danger">
                                Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                            </vx-button>
                        </div>
                    </vx-alert>

                    <template v-if="!loading && !loadError">
                        <div class="flex flex-col gap-2">
                            <div ref="sortableEl" class="flex flex-col gap-2">
                                <div
                                    v-for="(category, index) in categories"
                                    :key="category.id ?? category.name + '-' + index"
                                    class="category-draggable flex items-center"
                                >
                                    <span class="category-handle cursor-move pr-4 py-4">
                                        <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                                    </span>
                                    <intelligent-inbox-category-card
                                        class="grow"
                                        v-model="categories[index]!"
                                        @update:modelValue="updateCategory"
                                        @edit="openEdit(index)"
                                        @delete="removeCategory(index)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="flex">
                            <vx-box class="p-4">
                                <vx-button
                                    data-test="new-category-btn"
                                    v-if="categories.length < MAX_CATEGORIES"
                                    size="lg"
                                    @click="openCreate"
                                >
                                    New Category
                                    <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                                </vx-button>
                            </vx-box>
                        </div>

                        <vx-alert v-if="categories.length >= MAX_CATEGORIES" color="info" :closeable="false">
                            Maximum of {{ MAX_CATEGORIES }} categories reached.
                        </vx-alert>
                    </template>
                </div>
            </template>
        </vx-expandable-card>

        <modal-thread-category
            v-model:visible="modalVisible"
            :category="modalCategory"
            :editIndex="modalEditIndex"
            @saved="onCategorySaved"
        />
    </div>
</template>

<script lang="ts" setup>
import { faCirclePlus, faGripDotsVertical, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { useSortable } from '@vueuse/integrations/useSortable';
import { nextTick, ref, watch } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import IntelligentInboxCategoryCard from './IntelligentInboxCategoryCard.vue';
import ModalThreadCategory from './ModalThreadCategory.vue';
import { threadCategoriesClient } from '~/services';
import type {
    ThreadCategoryFormData,
    ThreadCategoryType,
} from '~/services/threadCategories/threadCategoriesClient.types';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';

const MAX_CATEGORIES = 20;
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const loading = ref(false);
const loadError = ref(false);
const categories = ref<ThreadCategoryType[]>([]);

const modalVisible = ref(false);
const modalCategory = ref<ThreadCategoryType | null>(null);
const modalEditIndex = ref<number | null>(null);

const openCreate = (): void => {
    modalCategory.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalCategory.value = categories.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const saveCategories = async (): Promise<void> => {
    const formData: ThreadCategoryFormData[] = categories.value.map((c) => ({
        name: c.name,
        description: c.description,
        classification_instructions: c.classification_instructions,
        color: c.color,
    }));

    await threadCategoriesClient.updateThreadCategories({ teamId }, { categories: formData });
};

const syncCategories = async (): Promise<void> => {
    try {
        await saveCategories();
    } catch (err) {
        toasted.error(catchRequestError(err));
        await fetchCategories();
    }
};

// currently only used to sync the color from the card popover
const updateCategory = async () => {
    await syncCategories();
    toasted.success('Category saved');
};

const removeCategory = async (index: number): Promise<void> => {
    const category = categories.value[index];

    VxSwal.confirmation('Are you sure?', `Are you sure you want to delete "${category?.name}"?`, 'danger', () => {
        categories.value.splice(index, 1);
        return saveCategories().catch((err) => {
            fetchCategories();
            return throwSwalRequestError(err);
        });
    });
};

const onCategorySaved = async (data: ThreadCategoryFormData, editIndex: number | null): Promise<void> => {
    if (null !== editIndex && undefined !== categories.value[editIndex]) {
        categories.value[editIndex] = { ...categories.value[editIndex], ...data };
    } else {
        categories.value.push({
            name: data.name,
            description: data.description,
            classification_instructions: data.classification_instructions,
            color: data.color,
        });
    }
    await syncCategories();
    toasted.success(null !== editIndex ? 'Category updated' : 'Category created');
};

const sortableEl = ref<HTMLElement | null>(null);

let sortableToastId: number | undefined;

const { start: startSortable } = useSortable(sortableEl, categories, {
    handle: '.category-handle',
    draggable: '.category-draggable',
    animation: 200,
    onUpdate: () => {
        syncCategories().then(() => {
            if (sortableToastId) {
                toasted.dismiss(sortableToastId);
            }

            sortableToastId = toasted.success('Categories sorted and saved');
        });
    },
} as Record<string, unknown>);

watch(loading, async (isLoading) => {
    if (!isLoading && !loadError.value) {
        await nextTick();
        startSortable();
    }
});

const fetchCategories = async (): Promise<void> => {
    loading.value = true;
    loadError.value = false;

    try {
        const response = await threadCategoriesClient.getThreadCategories({ teamId });
        categories.value = response.data.categories;
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

fetchCategories();
</script>
